import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Clock, Lightning, X } from "@phosphor-icons/react";

/**
 * Dashboard soft-paywall banner — shows when the user has 3 or fewer days
 * left on their free trial. Dismissible per-trial (remembers the trial end
 * date so dismissing once doesn't permanently silence it across consecutive
 * trial cycles).
 */
const DISMISS_KEY = "tf_trial_dismissed_until";

export default function TrialEndingBanner() {
  const { lang } = useI18n();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/pro/me").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!status?.trial_end_date) return;
    try {
      const dismissedFor = localStorage.getItem(DISMISS_KEY);
      if (dismissedFor && dismissedFor === status.trial_end_date) {
        setDismissed(true);
      }
    } catch { /* ignore */ }
  }, [status?.trial_end_date]);

  if (!status || !status.in_trial || dismissed) return null;
  const days = Math.max(0, status.trial_days_left ?? 0);
  // Only show during the final stretch.
  if (days > 3) return null;

  const dismiss = () => {
    try {
      if (status.trial_end_date) localStorage.setItem(DISMISS_KEY, status.trial_end_date);
    } catch { /* ignore */ }
    setDismissed(true);
  };

  const headline = (() => {
    if (days === 0) {
      return lang === "da" ? "Din prøveperiode slutter i dag." : "Your trial ends today.";
    }
    if (days === 1) {
      return lang === "da" ? "Sidste dag af din gratis prøveperiode." : "Last day of your free trial.";
    }
    return lang === "da"
      ? `Kun ${days} dage tilbage af din gratis prøve.`
      : `Only ${days} days left on your free trial.`;
  })();

  return (
    <div
      className="mb-8 flex items-start gap-4 border border-[#FFCC00]/60 bg-gradient-to-r from-[#FFCC00]/[0.12] to-[#FF9F0A]/[0.06] px-5 py-4 animate-fade-up"
      data-testid="trial-ending-banner"
    >
      <div className="w-10 h-10 flex items-center justify-center bg-[#FFCC00]/20 border border-[#FFCC00] shrink-0">
        <Clock weight="duotone" size={20} color="#FFCC00" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl tracking-tight" data-testid="trial-ending-headline">
          {headline.toUpperCase()}
        </div>
        <p className="text-sm text-zinc-300 mt-1">
          {lang === "da"
            ? "Vælg en plan nu for at beholde større hold, boost på Udforsk og Pro analytics — uden afbrydelse."
            : "Pick a plan now to keep larger crews, the Explore boost, and Pro analytics — uninterrupted."}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/pro"
          data-testid="trial-ending-cta"
          className="kinetic-btn bg-[#FFCC00] hover:bg-[#e6b800] text-black px-4 py-2.5 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
        >
          <Lightning weight="fill" size={14} />
          {lang === "da" ? "Vælg plan" : "Pick a plan"}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="trial-ending-dismiss"
          aria-label={lang === "da" ? "Afvis" : "Dismiss"}
          className="p-2 text-zinc-400 hover:text-white transition-colors"
        >
          <X weight="bold" size={16} />
        </button>
      </div>
    </div>
  );
}
